import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface DraggableCourseContentProps {
  /**
   * Unique identifier for the draggable item
   */
  id: string;
  /**
   * Whether dragging is disabled
   */
  isDisabled?: boolean;
  /**
   * Whether to hide the original item when dragging (for overlay effect)
   */
  hideOnDrag?: boolean;
  /**
   * Opacity when dragging (default: 1 for hideOnDrag, 0.5 otherwise)
   */
  dragOpacity?: number;
  /**
   * Content to render
   */
  children: React.ReactNode;
}

/**
 * Reusable draggable wrapper for course content (units, subunits, activities).
 * Uses @dnd-kit/sortable for drag-and-drop functionality.
 */
export default function DraggableCourseContent({
  id,
  isDisabled = false,
  hideOnDrag = false,
  dragOpacity,
  children,
}: DraggableCourseContentProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: isDisabled,
  });

  const defaultOpacity = hideOnDrag ? 0 : 0.5;
  const opacity = isDragging ? (dragOpacity ?? defaultOpacity) : 1;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity,
  };

  return (
    <div ref={setNodeRef} style={style} className='flex flex-col gap-2'>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement<any>, {
            dragAttributes: attributes,
            dragListeners: listeners,
          });
        }
        return child;
      })}
    </div>
  );
}
